import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import API_BASE_URL from '../api';

const EthnicityInfo = () => {
  const [ethnicityInfo, setEthnicityInfo] = useState({
    ethnicity: [],
    otherEthnicity: '',
    bornIn: '',
    otherBornIn: '',
    cultureImportance: 50, // Default value for the slider (0 = Not, 50 = Somewhat, 100 = Very)
  });
  const [showOtherEthnicity, setShowOtherEthnicity] = useState(false);
  const [showOtherBornIn, setShowOtherBornIn] = useState(false);
  const [message, setMessage] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);

  const getAuthToken = () => localStorage.getItem('stack_access_token');
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAuthToken();
    fetch(`${API_BASE_URL}/api/ethnicity-info`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Stack-Access-Token': token,
      },
    })
      .then((response) => {
        if (response.status === 404) {
          setIsNewUser(true);
          setMessage(
            'No ethnicity info found. Please set your ethnicity info.',
          );
          return;
        }
        if (!response.ok) {
          throw new Error('Failed to fetch ethnicity info');
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          const ethnicityData = data.reduce((acc, item) => {
            if (item.question_text === 'What is your ethnicity?') {
              acc.ethnicity = item.answer_text
                ? item.answer_text.split(',')
                : []; // Ensure it's an array
            }
            if (item.question_text === 'Where were you born?')
              acc.bornIn = item.answer_text;
            if (
              item.question_text ===
              'How important is it to date someone within your culture?'
            )
              acc.cultureImportance = item.answer_int;
            return acc;
          }, {});
          setEthnicityInfo(ethnicityData); // Set state with fetched data
        }
      })
      .catch((error) => {
        console.error('Error fetching ethnicity info:', error);
        setMessage('Error fetching ethnicity info.');
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = getAuthToken();

    // Ensure ethnicity is treated as an array or an empty string if it's not an array
    const ethnicityValue = Array.isArray(ethnicityInfo.ethnicity)
      ? ethnicityInfo.ethnicity.join(',')
      : ethnicityInfo.ethnicity;

    const payload = {
      ethnicity: showOtherEthnicity
        ? ethnicityInfo.otherEthnicity
        : ethnicityValue,
      otherEthnicity: ethnicityInfo.otherEthnicity,
      bornIn: showOtherBornIn
        ? ethnicityInfo.otherBornIn
        : ethnicityInfo.bornIn,
      otherBornIn: ethnicityInfo.otherBornIn,
      cultureImportance: parseInt(ethnicityInfo.cultureImportance, 10),
    };

    const method = isNewUser ? 'POST' : 'PUT';

    try {
      const response = await fetch(`${API_BASE_URL}/api/ethnicity-info`, {
        method: method,
        headers: {
          'X-Stack-Access-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update ethnicity info');
      }

      setEthnicityInfo(payload);
      setMessage('Ethnicity info updated successfully.');

      setTimeout(() => {
        navigate('/drinking-smoking-info');
      }, 2000);

      setIsNewUser(false);
    } catch (error) {
      console.error('Error setting ethnicity info:', error);
      setMessage('Failed to update ethnicity info.');
    }
  };

  const handleEthnicityChange = (e) => {
    const selectedOptions = Array.from(
      e.target.selectedOptions,
      (option) => option.value,
    );
    setEthnicityInfo({
      ...ethnicityInfo,
      ethnicity: selectedOptions,
    });
    setShowOtherEthnicity(selectedOptions.includes('Other'));
  };

  const handleChange = (e) => {
    setEthnicityInfo({
      ...ethnicityInfo,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#0D2329' }}
    >
      <Navbar />
      <div className="flex-1 flex justify-center items-center">
        <div
          className="w-full max-w-md p-8 rounded-lg shadow-md"
          style={{ backgroundColor: '#05664f' }}
        >
          <h1 className="text-4xl font-bold text-white text-center mb-6">
            Ethnicity Info
          </h1>
          {message && (
            <p className="text-sm text-red-200 text-center mb-4">{message}</p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-white">
              <label className="block text-sm font-medium">
                What is your ethnicity?
                <select
                  name="ethnicity"
                  value={ethnicityInfo.ethnicity}
                  onChange={handleEthnicityChange}
                  className="mt-1 block w-full px-3 py-2 text-base border border-gray-300 text-black focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  multiple
                >
                  <option value="Asian">Asian</option>
                  <option value="African">Black</option>
                  <option value="Hispanic">Hispanic</option>
                  <option value="White">White</option>
                  <option value="Native American">Native American</option>
                  <option value="Pacific Islander">Pacific Islander</option>
                  <option value="Middle Eastern">Middle Eastern</option>
                  <option value="Other">Other</option>
                </select>
              </label>
              {showOtherEthnicity && (
                <input
                  type="text"
                  name="otherEthnicity"
                  value={ethnicityInfo.otherEthnicity}
                  onChange={handleChange}
                  className="mt-2 block w-full px-3 py-2 text-base border border-gray-300 text-black focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  placeholder="Please specify"
                />
              )}
            </div>

            <div className="text-white">
              <label className="block text-sm font-medium">
                Where were you born?
                <select
                  name="bornIn"
                  value={ethnicityInfo.bornIn}
                  onChange={(e) => {
                    handleChange(e);
                    setShowOtherBornIn(e.target.value === 'Other');
                  }}
                  className="mt-1 block w-full px-3 py-2 text-base border border-gray-300 text-black focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="USA">USA</option>
                  <option value="Canada">Canada</option>
                  <option value="UK">UK</option>
                  <option value="Australia">Australia</option>
                  <option value="India">India</option>
                  <option value="China">China</option>
                  <option value="Other">Other</option>
                </select>
              </label>
              {showOtherBornIn && (
                <input
                  type="text"
                  name="otherBornIn"
                  value={ethnicityInfo.otherBornIn}
                  onChange={handleChange}
                  className="mt-2 block w-full px-3 py-2 text-base border border-gray-300 text-black focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  placeholder="Please specify"
                />
              )}
            </div>

            <div className="text-white">
              <label className="block text-sm font-medium">
                How important is it to date someone within your culture?
              </label>
              <input
                type="range"
                name="cultureImportance"
                min="0"
                max="100"
                value={ethnicityInfo.cultureImportance}
                onChange={handleChange}
                className="w-full"
              />
              <div className="flex justify-between">
                <span>Not</span>
                <span>Somewhat</span>
                <span>Very</span>
              </div>
            </div>

            <button
              type="submit"
              className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Next
            </button>

            <button
              type="button"
              className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mt-2"
              onClick={() => navigate('/kids-info')} // Navigate back to the previous form
            >
              Previous
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EthnicityInfo;
